import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiSave } from 'react-icons/fi';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import CountrySelector from '../components/CountrySelector';
import { apiService } from '../services/api';

interface InvoiceDetails {
  id: number;
  invoice_number: string;
  user_id: number;
  user_email: string;
  user_name: string;
  type: 'order' | 'invoice';
  status: 'pending' | 'completed' | 'cancelled';
  package_id: number;
  package_name: string;
  package_period: string;
  package_price: number;
  discount_amount: number;
  discount_percentage: number;
  tva_amount: number;
  amount: number;
  order_date: string;
  payment_datetime: string | null;
  payment_method: string | null;
  payment_id: string | null;
  client_details: {
    name_complete: string;
    email: string;
    phone: string;
    address: string;
    country: string;
  };
  organisation_details: {
    org_name: string;
    address: string;
    country: string;
    org_phone: string;
    zipcode: string;
    city: string;
    vat_number: string;
  };
  teamther_details: {
    company_name: string;
    address: string;
    zipcode: string;
    city: string;
    country: string;
    vat_number: string;
    vat_percentage: number;
  };
}

const EditInvoice: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [invoice, setInvoice] = useState<InvoiceDetails | null>(null);
  const [originalData, setOriginalData] = useState<any>(null);
  const [packages, setPackages] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [formData, setFormData] = useState({
    status: 'pending' as 'pending' | 'completed' | 'cancelled',
    type: 'order' as 'order' | 'invoice',
    package_id: 0,
    package_name: '',
    user_name: '',
    user_email: '',
    package_price: 0,
    discount_amount: 0,
    discount_percentage: 0,
    tva_amount: 0,
    payment_datetime: '',
    payment_method: '',
    payment_id: '',
    client_details: {
      name_complete: '',
      email: '',
      phone: '',
      address: '',
      country: '',
    },
    organisation_details: {
      org_name: '',
      address: '',
      country: '',
      org_phone: '',
      zipcode: '',
      city: '',
      vat_number: '',
    },
    teamther_details: {
      company_name: '',
      address: '',
      zipcode: '',
      city: '',
      country: '',
      vat_number: '',
      vat_percentage: 0,
    },
  });

  useEffect(() => {
    fetchCountries();
    fetchPackages();
  }, []);

  // Load invoice after countries are loaded
  useEffect(() => {
    if (countries.length > 0) {
      fetchInvoice();
    }
  }, [countries, id]);

  const fetchPackages = async () => {
    try {
      const response = await apiService.getPublicPackages();
      if (response.success) {
        setPackages(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching packages:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiService.getPublicCountries();
      if (response.success && response.data) {
        setCountries(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Helper function to get country name from ID or return as-is if already a name
  const getCountryName = (countryValue: any) => {
    if (!countryValue) return '';
    // If it's a number (ID), find the country name from loaded countries
    if (!isNaN(Number(countryValue))) {
      const country = countries.find(c => c.id === Number(countryValue));
      return country ? country.name : '';
    }
    // If it's already a string (name), return as-is
    return String(countryValue);
  };

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      const response = await apiService.getInvoiceById(Number(id));
      if (response.success) {
        const data = response.data;
        console.log('📦 Invoice data:', data);
        console.log('🌍 Countries loaded:', countries.length);
        console.log('📄 Packages loaded:', packages.length);
        console.log('🏳️ Client country:', data.client_details?.country);
        console.log('🏢 Org country:', data.organisation_details?.country);
        setInvoice(data);

        const initialData = {
          status: data.status,
          type: data.type,
          package_id: data.package_id,
          package_name: data.package_name,
          user_name: data.user_name || '',
          user_email: data.user_email || '',
          package_price: data.package_price,
          discount_amount: data.discount_amount,
          discount_percentage: data.discount_percentage,
          tva_amount: data.tva_amount,
          payment_datetime: data.payment_datetime || '',
          payment_method: data.payment_method || '',
          payment_id: data.payment_id || '',
          client_details: {
            name_complete: data.client_details?.name_complete || '',
            email: data.client_details?.email || '',
            phone: data.client_details?.phone || '',
            address: data.client_details?.address || '',
            country: getCountryName(data.client_details?.country),
          },
          organisation_details: {
            org_name: data.organisation_details?.org_name || '',
            address: data.organisation_details?.address || '',
            country: getCountryName(data.organisation_details?.country),
            org_phone: data.organisation_details?.org_phone || '',
            zipcode: data.organisation_details?.zipcode || '',
            city: data.organisation_details?.city || '',
            vat_number: data.organisation_details?.vat_number || '',
          },
          teamther_details: {
            company_name: data.teamther_details?.company_name || '',
            address: data.teamther_details?.address || '',
            zipcode: data.teamther_details?.zipcode || '',
            city: data.teamther_details?.city || '',
            country: getCountryName(data.teamther_details?.country),
            vat_number: data.teamther_details?.vat_number || '',
            vat_percentage: data.teamther_details?.vat_percentage || 0,
          },
        };
        console.log('✅ Processed form data:', initialData);
        setFormData(initialData);
        setOriginalData(initialData);
      }
    } catch (error: any) {
      setToast({ message: `Error fetching invoice: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Get list of modified fields
    const modifiedFields = getModifiedFields();

    // Show confirmation if there are changes
    if (modifiedFields.length > 0) {
      const confirmMessage = `You have modified the following fields:\n\n${modifiedFields.map(f => `• ${f}`).join('\n')}\n\nDo you want to save these changes?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    setSaving(true);

    try {
      await apiService.updateInvoice(Number(id), formData);
      setToast({ message: 'Invoice updated successfully', type: 'success' });
      setTimeout(() => {
        navigate('/subscriptions');
      }, 1500);
    } catch (error: any) {
      setToast({ message: `Error updating invoice: ${error.message}`, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const calculateTotal = () => {
    const subtotal = formData.package_price - formData.discount_amount;
    return subtotal + formData.tva_amount;
  };

  // Check if a field has been modified
  const isFieldModified = (fieldPath: string, value: any) => {
    if (!originalData) return false;
    const keys = fieldPath.split('.');
    let originalValue = originalData;
    for (const key of keys) {
      originalValue = originalValue?.[key];
    }
    return originalValue !== value;
  };

  // Handle package price change
  const handlePackagePriceChange = (value: number) => {
    const newPrice = value || 0;
    const newDiscountAmount = (newPrice * formData.discount_percentage) / 100;
    setFormData({
      ...formData,
      package_price: newPrice,
      discount_amount: newDiscountAmount,
    });
  };

  // Handle discount percentage change
  const handleDiscountPercentageChange = (value: number) => {
    const percentage = value || 0;
    const newDiscountAmount = (formData.package_price * percentage) / 100;
    setFormData({
      ...formData,
      discount_percentage: percentage,
      discount_amount: newDiscountAmount,
    });
  };

  // Handle discount amount change
  const handleDiscountAmountChange = (value: number) => {
    const amount = value || 0;
    const newPercentage = formData.package_price > 0 ? (amount / formData.package_price) * 100 : 0;
    setFormData({
      ...formData,
      discount_amount: amount,
      discount_percentage: Number(newPercentage.toFixed(2)),
    });
  };

  // Handle package selection change
  const handlePackageChange = (packageId: number) => {
    const selectedPackage = packages.find(pkg => pkg.id === packageId);
    if (selectedPackage) {
      const newPrice = selectedPackage.price || formData.package_price;
      const newDiscountAmount = (newPrice * formData.discount_percentage) / 100;
      setFormData({
        ...formData,
        package_id: selectedPackage.id,
        package_name: selectedPackage.name,
        package_price: newPrice,
        discount_amount: newDiscountAmount,
      });
    }
  };

  // Get list of modified fields for summary
  const getModifiedFields = () => {
    if (!originalData) return [];
    const changes: string[] = [];

    if (formData.package_price !== originalData.package_price) changes.push('Package Price');
    if (formData.discount_percentage !== originalData.discount_percentage) changes.push('Discount %');
    if (formData.discount_amount !== originalData.discount_amount) changes.push('Discount Amount');
    if (formData.tva_amount !== originalData.tva_amount) changes.push('TVA Amount');
    if (formData.user_name !== originalData.user_name) changes.push('User Name');
    if (formData.user_email !== originalData.user_email) changes.push('User Email');
    if (formData.package_id !== originalData.package_id) changes.push('Package');
    if (formData.status !== originalData.status) changes.push('Status');
    if (formData.type !== originalData.type) changes.push('Type');

    return changes;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-500 border-r-transparent"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading invoice...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 dark:text-gray-400">Invoice not found</p>
            <button
              onClick={() => navigate('/subscriptions')}
              className="mt-4 text-brand-500 hover:text-brand-600"
            >
              Back to Subscriptions
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/subscriptions')}
            className="mb-4 flex items-center text-gray-600 transition hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <FiArrowLeft className="mr-2 h-5 w-5" />
            Back to Subscriptions
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Edit Invoice
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {invoice.invoice_number}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Changes Summary Card */}
          {getModifiedFields().length > 0 && (
            <div className="mb-6 rounded-lg border-2 border-orange-200 bg-orange-50 p-4 dark:border-orange-800 dark:bg-orange-900/20">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-medium text-orange-800 dark:text-orange-300">
                    You have unsaved changes
                  </h3>
                  <div className="mt-2 text-sm text-orange-700 dark:text-orange-400">
                    <p className="mb-2">The following fields have been modified:</p>
                    <div className="flex flex-wrap gap-2">
                      {getModifiedFields().map((field, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/40 dark:text-orange-300"
                        >
                          {field}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Left Column - Invoice Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* User Info Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  User Information
                </h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Name
                    </label>
                    <input
                      type="text"
                      value={formData.user_name}
                      onChange={(e) => setFormData({ ...formData, user_name: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.user_email}
                      onChange={(e) => setFormData({ ...formData, user_email: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>

              {/* Client Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Client Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={formData.client_details.name_complete}
                      onChange={(e) => setFormData({
                        ...formData,
                        client_details: { ...formData.client_details, name_complete: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email
                      </label>
                      <input
                        type="email"
                        value={formData.client_details.email}
                        onChange={(e) => setFormData({
                          ...formData,
                          client_details: { ...formData.client_details, email: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Phone
                      </label>
                      <input
                        type="text"
                        value={formData.client_details.phone}
                        onChange={(e) => setFormData({
                          ...formData,
                          client_details: { ...formData.client_details, phone: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.client_details.address}
                      onChange={(e) => setFormData({
                        ...formData,
                        client_details: { ...formData.client_details, address: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Country
                    </label>
                    <div className="mt-1">
                      <CountrySelector
                        countries={countries}
                        value={formData.client_details.country}
                        onChange={(name) => setFormData({
                          ...formData,
                          client_details: { ...formData.client_details, country: name }
                        })}
                        loading={loadingCountries}
                        disabled={loadingCountries}
                        placeholder="Select country..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Organization Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Organization Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Organization Name
                    </label>
                    <input
                      type="text"
                      value={formData.organisation_details.org_name}
                      onChange={(e) => setFormData({
                        ...formData,
                        organisation_details: { ...formData.organisation_details, org_name: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.organisation_details.address}
                      onChange={(e) => setFormData({
                        ...formData,
                        organisation_details: { ...formData.organisation_details, address: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.organisation_details.city}
                        onChange={(e) => setFormData({
                          ...formData,
                          organisation_details: { ...formData.organisation_details, city: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Zipcode
                      </label>
                      <input
                        type="text"
                        value={formData.organisation_details.zipcode}
                        onChange={(e) => setFormData({
                          ...formData,
                          organisation_details: { ...formData.organisation_details, zipcode: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Country
                      </label>
                      <div className="mt-1">
                        <CountrySelector
                          countries={countries}
                          value={formData.organisation_details.country}
                          onChange={(name) => setFormData({
                            ...formData,
                            organisation_details: { ...formData.organisation_details, country: name }
                          })}
                          loading={loadingCountries}
                          disabled={loadingCountries}
                          placeholder="Select country..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Organization Phone
                      </label>
                      <input
                        type="text"
                        value={formData.organisation_details.org_phone}
                        onChange={(e) => setFormData({
                          ...formData,
                          organisation_details: { ...formData.organisation_details, org_phone: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        VAT Number
                      </label>
                      <input
                        type="text"
                        value={formData.organisation_details.vat_number}
                        onChange={(e) => setFormData({
                          ...formData,
                          organisation_details: { ...formData.organisation_details, vat_number: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Teamther Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Teamther Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={formData.teamther_details.company_name}
                      onChange={(e) => setFormData({
                        ...formData,
                        teamther_details: { ...formData.teamther_details, company_name: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.teamther_details.address}
                      onChange={(e) => setFormData({
                        ...formData,
                        teamther_details: { ...formData.teamther_details, address: e.target.value }
                      })}
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        City
                      </label>
                      <input
                        type="text"
                        value={formData.teamther_details.city}
                        onChange={(e) => setFormData({
                          ...formData,
                          teamther_details: { ...formData.teamther_details, city: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Zipcode
                      </label>
                      <input
                        type="text"
                        value={formData.teamther_details.zipcode}
                        onChange={(e) => setFormData({
                          ...formData,
                          teamther_details: { ...formData.teamther_details, zipcode: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Country
                      </label>
                      <input
                        type="text"
                        value={formData.teamther_details.country}
                        onChange={(e) => setFormData({
                          ...formData,
                          teamther_details: { ...formData.teamther_details, country: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        VAT Number
                      </label>
                      <input
                        type="text"
                        value={formData.teamther_details.vat_number}
                        onChange={(e) => setFormData({
                          ...formData,
                          teamther_details: { ...formData.teamther_details, vat_number: e.target.value }
                        })}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        VAT Percentage (%)
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.teamther_details.vat_percentage}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d*$/.test(v)) {
                            setFormData({
                              ...formData,
                              teamther_details: { ...formData.teamther_details, vat_percentage: v === '' ? 0 : parseFloat(v) || 0 }
                            });
                          }
                        }}
                        className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Package Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Package Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>Select Package</span>
                      {isFieldModified('package_id', formData.package_id) && (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                          Modified
                        </span>
                      )}
                    </label>
                    <select
                      value={formData.package_id}
                      onChange={(e) => handlePackageChange(Number(e.target.value))}
                      className={`mt-1 w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 dark:text-white ${isFieldModified('package_id', formData.package_id)
                          ? 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500/20 dark:border-orange-600 dark:bg-orange-900/10 dark:bg-navy-700'
                          : 'border-gray-300 bg-white focus:border-brand-500 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700'
                        }`}
                    >
                      {packages.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {pkg.title} - ${pkg.monthly_price || pkg.price_1_month || 0}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Current: {formData.package_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Pricing Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Pricing Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>Package Price ($)</span>
                      {isFieldModified('package_price', formData.package_price) && (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                          Modified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.package_price}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                          handlePackagePriceChange(v === '' ? 0 : parseFloat(v) || 0);
                        }
                      }}
                      className={`mt-1 w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 dark:bg-navy-700 dark:text-white ${isFieldModified('package_price', formData.package_price)
                          ? 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500/20 dark:border-orange-600 dark:bg-orange-900/10'
                          : 'border-gray-300 bg-white focus:border-brand-500 focus:ring-brand-500/20 dark:border-navy-600'
                        }`}
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                        <span>Discount Amount ($)</span>
                        {isFieldModified('discount_amount', formData.discount_amount) && (
                          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                            Modified
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.discount_amount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d*$/.test(v)) {
                            handleDiscountAmountChange(v === '' ? 0 : parseFloat(v) || 0);
                          }
                        }}
                        className={`mt-1 w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 dark:bg-navy-700 dark:text-white ${isFieldModified('discount_amount', formData.discount_amount)
                            ? 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500/20 dark:border-orange-600 dark:bg-orange-900/10'
                            : 'border-gray-300 bg-white focus:border-brand-500 focus:ring-brand-500/20 dark:border-navy-600'
                          }`}
                      />
                    </div>
                    <div>
                      <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                        <span>Discount Percentage (%)</span>
                        {isFieldModified('discount_percentage', formData.discount_percentage) && (
                          <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                            Modified
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.discount_percentage}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === '' || /^\d*\.?\d*$/.test(v)) {
                            handleDiscountPercentageChange(v === '' ? 0 : parseFloat(v) || 0);
                          }
                        }}
                        className={`mt-1 w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 dark:bg-navy-700 dark:text-white ${isFieldModified('discount_percentage', formData.discount_percentage)
                            ? 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500/20 dark:border-orange-600 dark:bg-orange-900/10'
                            : 'border-gray-300 bg-white focus:border-brand-500 focus:ring-brand-500/20 dark:border-navy-600'
                          }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300">
                      <span>TVA/Tax Amount ($)</span>
                      {isFieldModified('tva_amount', formData.tva_amount) && (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                          Modified
                        </span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.tva_amount}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '' || /^\d*\.?\d*$/.test(v)) {
                          setFormData({ ...formData, tva_amount: v === '' ? 0 : parseFloat(v) || 0 });
                        }
                      }}
                      className={`mt-1 w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2 dark:bg-navy-700 dark:text-white ${isFieldModified('tva_amount', formData.tva_amount)
                          ? 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-orange-500/20 dark:border-orange-600 dark:bg-orange-900/10'
                          : 'border-gray-300 bg-white focus:border-brand-500 focus:ring-brand-500/20 dark:border-navy-600'
                        }`}
                    />
                  </div>

                  {/* Calculation Breakdown */}
                  <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-900">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Package Price:</span>
                      <span className="font-medium text-gray-900 dark:text-white">${formData.package_price.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Discount ({formData.discount_percentage}%):</span>
                      <span className="font-medium text-red-600 dark:text-red-400">-${formData.discount_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                      <span className="font-medium text-gray-900 dark:text-white">${(formData.package_price - formData.discount_amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">TVA/Tax:</span>
                      <span className="font-medium text-gray-900 dark:text-white">+${formData.tva_amount.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-gray-300 pt-2 dark:border-navy-600">
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-gray-900 dark:text-white">Total Amount:</span>
                        <span className="text-brand-600 dark:text-brand-400">
                          ${calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Details Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Payment Details
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Payment Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.payment_datetime}
                      onChange={(e) =>
                        setFormData({ ...formData, payment_datetime: e.target.value })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Payment Method
                    </label>
                    <input
                      type="text"
                      value={formData.payment_method}
                      onChange={(e) =>
                        setFormData({ ...formData, payment_method: e.target.value })
                      }
                      placeholder="e.g., Credit Card, PayPal"
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Payment ID / Transaction ID
                    </label>
                    <input
                      type="text"
                      value={formData.payment_id}
                      onChange={(e) =>
                        setFormData({ ...formData, payment_id: e.target.value })
                      }
                      placeholder="Transaction ID"
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Type & Actions */}
            <div className="space-y-6">
              {/* Status & Type Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Status & Type
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({ ...formData, status: e.target.value as any })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Type
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value as any })
                      }
                      className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                      <option value="order">Order</option>
                      <option value="invoice">Invoice</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Actions Card */}
              <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
                <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                  Actions
                </h2>
                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex w-full items-center justify-center space-x-2 rounded-lg bg-brand-500 px-4 py-3 font-medium text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <FiSave className="h-5 w-5" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/subscriptions')}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-700 dark:text-gray-300 dark:hover:bg-navy-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>

            </div>
          </div>
        </form>

        {/* Toast Notification */}
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </div>
    </Layout>
  );
};

export default EditInvoice;
